import { useState } from 'react'
import { LogOut, User } from 'lucide-react'
import { tokens } from '@/theme'
import { TrelleborgMark } from './TrelleborgMark'
import { MOBILE_MAX } from './responsive'
import type { NavTab } from '@/domain/types'

interface Props {
  active: NavTab
  onChange: (tab: NavTab) => void
  userName?: string
  onSignOut: () => void
}

/**
 * Three tabs. `home` and `domains` are routes without one: home is reached
 * by the mark, and domains by cross-link from an initiative or capability.
 * Neither reads as active while you're there.
 *
 * The Funnel tab is gone with the board page. The funnel itself did not go
 * anywhere — it is the explainer and the stage filter in the Initiatives
 * hero, which is where someone asking "how does this move" already is.
 */
const NAV: { id: NavTab; label: string }[] = [
  { id: 'chat', label: 'Chat' },
  { id: 'initiatives', label: 'Initiatives' },
  { id: 'capabilities', label: 'Capabilities' },
]

/**
 * Phone chrome. Three things have to give at 360px, and this is the order
 * they give in: the signed-in name (the account is one tap away in the
 * sign-out anyway), the wordmark beside the glyph, and the nav's breathing
 * room. Past that the nav scrolls sideways rather than wrapping the header
 * onto a second line — a sticky header is a tax on every scroll, and it
 * should not double in height to hold three words.
 */
const headerStyles = `
@media (max-width: ${MOBILE_MAX}px) {
  .orion-header-inner { height: 56px !important; gap: 10px !important; }
  .orion-header-inner .orion-wordmark { display: none; }
  .orion-header-nav {
    justify-content: flex-start;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .orion-header-nav::-webkit-scrollbar { display: none; }
  .orion-header-tab { padding: 17px 8px !important; font-size: 10px !important; letter-spacing: 0.08em !important; }
  .orion-header-tab > span { left: 8px !important; right: 8px !important; bottom: 12px !important; }
}
`

/**
 * Site chrome. The ink panel is the app's header on every route — each page
 * opens with a dark band that this sits flush against, so a paper header
 * would draw a line across the top of every hero.
 */
export function SiteHeader({ active, onChange, userName, onSignOut }: Props) {
  const [hoverId, setHoverId] = useState<NavTab | null>(null)

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: tokens.inkPanel,
        borderBottom: `1px solid ${tokens.inkPanelLine}`,
      }}
    >
      <style>{headerStyles}</style>
      <div
        className="orion-header-inner orion-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `0 ${tokens.containerPadX}px`,
          height: tokens.headerH,
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
          alignItems: 'center',
          gap: 32,
        }}
      >
        <TrelleborgMark variant="dark" size={44} onClick={() => onChange('home')} />

        <nav
          className="orion-header-nav"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
        >
          {NAV.map(({ id, label }) => {
            const isActive = active === id
            const isHov = hoverId === id && !isActive
            return (
              <button
                key={id}
                className="orion-header-tab"
                onClick={() => onChange(id)}
                onMouseEnter={() => setHoverId(id)}
                onMouseLeave={() => setHoverId(null)}
                style={{
                  position: 'relative',
                  flex: 'none',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '22px 14px',
                  fontFamily: tokens.fontDisplay,
                  fontSize: 11.5,
                  fontWeight: isActive ? tokens.weightDemi : tokens.weightMedium,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  color: isActive || isHov ? tokens.paper : tokens.inkPanelText,
                  transition: `color ${tokens.durFast} ${tokens.easeStandard}`,
                }}
              >
                {label}
                <span
                  style={{
                    position: 'absolute',
                    left: 14,
                    right: 14,
                    bottom: 18,
                    height: 2,
                    background: tokens.gold,
                    transformOrigin: 'left center',
                    transform: isActive ? 'scaleX(1)' : isHov ? 'scaleX(0.35)' : 'scaleX(0)',
                    transition: `transform ${tokens.durStd} ${tokens.easeStandard}`,
                  }}
                />
              </button>
            )
          })}
        </nav>

        {/* User + sign-out */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {userName && (
            <div
              className="orion-sm-hide"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: tokens.inkPanelText,
                fontFamily: tokens.fontDisplay,
                fontWeight: tokens.weightMedium,
              }}
            >
              <User size={14} strokeWidth={1.6} />
              <span
                style={{
                  maxWidth: 180,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userName}
              </span>
            </div>
          )}
          <div
            className="orion-sm-hide"
            style={{ width: 1, height: 22, background: tokens.inkPanelLine, margin: '0 2px' }}
          />
          <SignOutButton onSignOut={onSignOut} />
        </div>
      </div>
    </header>
  )
}

function SignOutButton({ onSignOut }: { onSignOut: () => void }) {
  const [hov, setHov] = useState(false)
  return (
    <button
      aria-label="Sign out"
      onClick={onSignOut}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 34,
        height: 34,
        background: hov ? tokens.inkPanel2 : 'transparent',
        border: 'none',
        borderRadius: tokens.radiusMd,
        cursor: 'pointer',
        color: hov ? tokens.paper : tokens.inkPanelText,
        transition: `background ${tokens.durFast} ${tokens.easeStandard}, color ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      <LogOut size={15} strokeWidth={1.6} />
    </button>
  )
}
